import { CenteredCircularProgress } from '../../common/CenteredCircularProgress';
import { AutomationDialogContent } from '../../Automations/AutomationDialogContent';
import { AutomationFormVariant } from '../../../types/enums/AutomationFormVariant';
import { useSelector } from 'react-redux';
import { selectDefaultCreationEnvironment } from '../environments/environmentsReducer';
import { selectDedup, selectMatching } from '../../../reducers/global/dedupMatchingReducers';
import useDedupMatching from './useDedupMatching';
import { useCallback, useMemo } from 'react';
import { DedupMatchingType } from '../../../types/enums/DedupMatchingType';
import { dedupMatchingSortOptions } from '../../Automations/consts';
import {
  AutomationsContextProvider,
  AutomationsContextType,
} from '../../Automations/AutomationsContext';
import { AutomationType } from '../../../types/enums/AutomationType';
import DrawerObjectsImage from '../../../components/common/empty-state/svgs/drawerObjects.svg?react';
import { UI_EVENTS } from '../../../services/events';
import { useAutomationsApiFacade } from '../../../apis/facades/useAutomationsApiFacade';
import { DataTableId } from '../../common/table/utils';
import { SaveOrCreateAutomationParams } from '../../Automations/useAutomationApiPerType';
import { Filters, FilterComponentType } from '../../common/filters/filters-renderer/types';
import { AutomationFiltersTypesMap } from '../../Automations/filters/filtersTypes';
import { activeFilterItems } from '../../Automations/filters/helpers';

export const dedupAutomationContextData: AutomationsContextType = {
  automationType: AutomationType.Dedupe,
  permissions: {
    create: 'create:dedupe-matching',
    edit: 'edit:dedupe-matching',
    deploy: 'deploy:dedupe-matching',
    delete: 'delete:dedupe-matching',
    notifyAdmin: 'edit:dedupe-matching:notify-admin',
  },
  emptyStateTitle: 'Create your first matching and dedupe operation',
  emptyStateImage: <DrawerObjectsImage />,
  defaultName: 'New Dedupe',
  placeholder: 'New Dedupe',
  nameInMessage: 'deduplication and matching rules',
  nameInMessageSingular: 'deduplication / matching rule',
  openEvent: UI_EVENTS.matchingDedupeOpen,
  automationVariant: AutomationFormVariant.DEDUP_MATCHING,
  templateSupport: false, //dedup/matching templates are implemented differently than the other types of automations
  requestDeployNotifyAdmin: async () => {},
  impactAnalysisAiSupport: false,
  tableId: DataTableId.AutomationDedupMatchingTable,
};

export const DEDUP_OPERATIONS = [
  DedupMatchingType.LEAD_TO_LEAD_DEDUP,
  DedupMatchingType.CONTACT_TO_CONTACT_DEDUP,
  DedupMatchingType.ACCOUNT_TO_ACCOUNT_DEDUP,
  DedupMatchingType.LEAD_TO_CONTACT_DEDUP,
];
export const MATCHING_OPERATIONS = [
  DedupMatchingType.LEAD_TO_LEAD_MATCHING,
  DedupMatchingType.LEAD_TO_ACCOUNT_MATCHING,
];

export const dedupMatchingFilters: Filters = {
  [AutomationFiltersTypesMap.operations]: {
    type: FilterComponentType.MULTI_SELECT,
    label: 'Operations',
    items: [
      //if new added DEDUP_OPERATIONS should be updated
      { value: 'deduplication_section', label: 'Deduplication', isSectionItem: true },
      { value: DedupMatchingType.LEAD_TO_LEAD_DEDUP, label: 'Lead to Lead' },
      { value: DedupMatchingType.CONTACT_TO_CONTACT_DEDUP, label: 'Contact to Contact' },
      { value: DedupMatchingType.ACCOUNT_TO_ACCOUNT_DEDUP, label: 'Account to Account' },
      { value: DedupMatchingType.LEAD_TO_CONTACT_DEDUP, label: 'Lead to Contact' },
      //if new added DEDUP_OPERATIONS should be updated
      { value: 'matching_section', label: 'Matching', isSectionItem: true },
      { value: DedupMatchingType.LEAD_TO_LEAD_MATCHING, label: 'Lead to Lead' },
      { value: DedupMatchingType.LEAD_TO_ACCOUNT_MATCHING, label: 'Lead to Account' },
    ],
  },
  [AutomationFiltersTypesMap.status]: {
    type: FilterComponentType.MULTI_SELECT,
    label: 'Status',
    items: activeFilterItems,
  },
};

const DedupMatching = () => {
  const dedupList = useSelector(selectDedup);
  const matchingList = useSelector(selectMatching);

  const dedupMatchingList = useMemo(
    () => (dedupList && matchingList ? [...dedupList, ...matchingList] : undefined),
    [dedupList, matchingList],
  );

  const crmOrg = useSelector(selectDefaultCreationEnvironment);
  const {
    editDedupMatching,
    createDedupMatching,
    deleteDedupMatching,
    toggleDedupMatching,
    deployDedupMatching,
  } = useDedupMatching();

  const onSaveOrCreate = useCallback(
    async ({ automation, crmOrgIds }: SaveOrCreateAutomationParams) => {
      const toSend = {
        ...automation,
        deployToOrgIds: crmOrgIds,
      };
      const isEdit = !!automation?.automationId;

      if (isEdit) {
        return await editDedupMatching({
          deployToOrgIds: crmOrgIds,
          updateAutomationDto: toSend,
          versionId: toSend.versionId,
        });
      } else {
        return await createDedupMatching({
          deployToOrgIds: crmOrgIds,
          createAutomationDto: toSend,
        });
      }
    },
    [createDedupMatching, editDedupMatching],
  );

  const onToggleActivation = useCallback(
    async (
      toggleAssignmentDto: AutomationStructureNew,
      deployToOrgIds: string[],
      isActive: boolean,
    ) => {
      const toSend = {
        ...toggleAssignmentDto,
        deployToOrgIds,
        isActive,
      };
      await toggleDedupMatching({
        deployToOrgIds,
        updateAutomationDto: toSend,
        versionId: toSend.versionId,
      });
    },
    [toggleDedupMatching],
  );

  const onDeleteAutomation = useCallback(
    async (automationId: string, automationType: AutomationType) => {
      await deleteDedupMatching({ automationId, automationType });
    },
    [deleteDedupMatching],
  );

  const onDeployAutomation = useCallback(
    async (deployAutomationDto: AutomationStructureNew, deployToOrgIds: string[]) => {
      const toSend = {
        ...deployAutomationDto,
        deployToOrgIds,
      };
      return await deployDedupMatching({
        deployAutomationDto: toSend,
        automationId: toSend.automationId,
        versionId: toSend.versionId,
        deployToOrgIds,
      });
    },
    [deployDedupMatching],
  );

  const { patch_dedupe_matching_admin_notification } = useAutomationsApiFacade();
  const _dedupAutomationContextData: AutomationsContextType = useMemo(() => {
    return {
      ...dedupAutomationContextData,
      requestDeployNotifyAdmin: patch_dedupe_matching_admin_notification,
    };
  }, [patch_dedupe_matching_admin_notification]);

  if (!dedupMatchingList) return <CenteredCircularProgress />;

  return (
    <>
      {crmOrg && (
        <AutomationsContextProvider value={_dedupAutomationContextData}>
          <AutomationDialogContent
            dataTestId="dedupe-matching-page"
            automations={dedupMatchingList ?? []}
            onDeleteAutomation={onDeleteAutomation}
            crmOrgId={crmOrg.id}
            onSaveOrCreate={onSaveOrCreate}
            onToggleActivation={onToggleActivation}
            sortOptions={dedupMatchingSortOptions}
            onDeployAutomation={onDeployAutomation}
            filters={dedupMatchingFilters}
          />
        </AutomationsContextProvider>
      )}
    </>
  );
};

export default DedupMatching;
